/**
 * Analytics API client — all analytics functions consolidated here.
 * Includes closed-loop analytics, performance analytics, zone management,
 * risk assessment, and compliance functions.
 *
 * Track 6.3: Functions migrated from api.ts for module separation.
 */

import { log } from './logger';
import { format, subDays } from "date-fns";
import {
  activitiesApi,
  closedLoopApi,
  engagementApi,
  insightsApi,
  settingsApi,
  zonesApi,
} from "./api/typedClient";
import type {
  LoadCurvesResponse,
  AdherenceDashboard,
  TimeInZoneAnalysis,
  PlanProjection,
  InjuryRiskIndicator,
  PerformanceTrend,
  TrendsResponse,
  ZoneSystem,
  ZoneBootstrapResponse,
} from "@/types/closedLoopAnalytics";

// ── Profile Completeness ────────────────────────────────────────────────────

export interface ProfileCompletenessData {
  hasExperience: boolean;
  hasInjuryInfo: boolean;
  hasRaceTarget: boolean;
}

export async function fetchProfileCompleteness(): Promise<ProfileCompletenessData> {
  log.debug("API", "Fetching profile completeness");
  try {
    // Fetch from multiple sources: onboarding profile JSONB + settings-written data
    const [profileRes, prefsRes, meProfileRes] = await Promise.all([
      engagementApi.getProfile().catch(() => null),
      settingsApi.getTrainingPreferences().catch(() => null),
      settingsApi.getProfile().catch(() => null),
    ]);

    // Onboarding JSONB fields (set during onboarding)
    const profileData = profileRes?.data;
    const onboardExperience = profileData?.training_context?.experience_level;
    const onboardInjury = profileData?.constraints?.injury_status;
    const onboardRaceDate = profileData?.goals?.target_date;

    // Settings-written fields (set via TrainingPreferencesSection)
    const prefsData = prefsRes?.data ?? prefsRes;
    const settingsYears = (prefsData as Record<string, unknown>)?.years_of_training as number | undefined;
    const settingsInjury = (prefsData as Record<string, unknown>)?.injury_history as boolean | undefined;
    const settingsInjuryNotes = (prefsData as Record<string, unknown>)?.injury_notes as string | undefined;

    // /me/profile fields (target_event.date from updateUserProfile)
    const meData = meProfileRes?.data ?? meProfileRes;
    const meTargetDate = (meData as Record<string, unknown>)?.target_event
      ? ((meData as Record<string, unknown>).target_event as { date?: string })?.date
      : undefined;

    // Merge: complete if EITHER source says so
    const hasExperience =
      (!!onboardExperience && onboardExperience !== "unknown" && onboardExperience !== "beginner") ||
      (typeof settingsYears === "number" && settingsYears >= 1);

    const hasInjuryInfo =
      (!!onboardInjury && onboardInjury !== "none") ||
      (settingsInjury === true && !!settingsInjuryNotes);

    const hasRaceTarget = !!onboardRaceDate || !!meTargetDate;

    return { hasExperience, hasInjuryInfo, hasRaceTarget };
  } catch (error) {
    log.warn("API", "Failed to fetch profile completeness, using defaults:", error);
    return { hasExperience: false, hasInjuryInfo: false, hasRaceTarget: false };
  }
}

// ── Engagement Analytics ────────────────────────────────────────────────────

export interface StreakData {
  current_streak: number;
  longest_streak: number;
  last_active_date: string | null;
}

export interface ConsistencyData {
  score: number;
  completed_sessions: number;
  planned_sessions: number;
  window_weeks: number;
}

export interface WeeklyCompletionData {
  completed: number;
  planned: number;
  percentage: number;
}

export async function fetchStreak(): Promise<StreakData> {
  log.debug("API", "Fetching streak");
  try {
    const response = await engagementApi.getStreak();
    return response.data as StreakData;
  } catch (error) {
    log.error("API", "Failed to fetch streak:", error);
    throw error;
  }
}

export async function fetchConsistencyScore(): Promise<ConsistencyData> {
  log.debug("API", "Fetching consistency score");
  try {
    const response = await engagementApi.getConsistencyScore();
    return response.data as ConsistencyData;
  } catch (error) {
    log.error("API", "Failed to fetch consistency score:", error);
    throw error;
  }
}

export async function fetchWeeklyCompletion(): Promise<WeeklyCompletionData> {
  log.debug("API", "Fetching weekly completion");
  try {
    const response = await engagementApi.getWeeklyCompletion();
    return response.data as WeeklyCompletionData;
  } catch (error) {
    log.error("API", "Failed to fetch weekly completion:", error);
    throw error;
  }
}

// ── Closed-Loop Analytics ───────────────────────────────────────────────────

export async function fetchLoadCurves(
  start?: string,
  end?: string,
): Promise<LoadCurvesResponse> {
  log.debug("API", "Fetching load curves");
  try {
    const params: Record<string, string> = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const response = await closedLoopApi.getLoadCurves(params);
    return response.data as LoadCurvesResponse;
  } catch (error) {
    log.error("API", "Failed to fetch load curves:", error);
    throw error;
  }
}

export async function fetchAdherence(
  weeks?: number,
): Promise<AdherenceDashboard> {
  log.debug("API", "Fetching adherence");
  try {
    const params: Record<string, number> = {};
    if (weeks) params.weeks = weeks;
    const response = await closedLoopApi.getAdherence(params);
    return response.data as AdherenceDashboard;
  } catch (error) {
    log.error("API", "Failed to fetch adherence:", error);
    throw error;
  }
}

export async function fetchTimeInZone(
  start?: string,
  end?: string,
  metric?: string,
): Promise<TimeInZoneAnalysis> {
  log.debug("API", "Fetching time in zone");
  try {
    const params: Record<string, string> = {};
    if (start) params.start = start;
    if (end) params.end = end;
    if (metric) params.metric = metric;
    const response = await closedLoopApi.getTimeInZone(params);
    return response.data as TimeInZoneAnalysis;
  } catch (error) {
    log.error("API", "Failed to fetch time in zone:", error);
    throw error;
  }
}

export async function fetchProjection(
  weeks?: number,
  raceDate?: string,
): Promise<PlanProjection> {
  log.debug("API", "Fetching projection");
  try {
    const params: Record<string, string | number> = {};
    if (weeks) params.weeks = weeks;
    if (raceDate) params.race_date = raceDate;
    const response = await closedLoopApi.getProjection(params);
    return response.data as PlanProjection;
  } catch (error) {
    log.error("API", "Failed to fetch projection:", error);
    throw error;
  }
}

export async function fetchInjuryRisk(): Promise<InjuryRiskIndicator> {
  log.debug("API", "Fetching injury risk");
  try {
    const response = await closedLoopApi.getInjuryRisk();
    return response.data as InjuryRiskIndicator;
  } catch (error) {
    log.error("API", "Failed to fetch injury risk:", error);
    throw error;
  }
}

export async function fetchTrends(
  metrics?: string,
  weeks?: number,
): Promise<TrendsResponse> {
  log.debug("API", "Fetching trends");
  try {
    const params: Record<string, string | number> = {};
    if (metrics) params.metrics = metrics;
    if (weeks) params.weeks = weeks;
    const response = await closedLoopApi.getTrends(params);
    return response.data as TrendsResponse;
  } catch (error) {
    log.error("API", "Failed to fetch trends:", error);
    throw error;
  }
}

// ── Zone Management ─────────────────────────────────────────────────────────

export async function fetchZones(): Promise<ZoneSystem[]> {
  log.debug("API", "Fetching zones");
  try {
    const response = await zonesApi.list();
    return response.data as ZoneSystem[];
  } catch (error) {
    log.error("API", "Failed to fetch zones:", error);
    throw error;
  }
}

export async function fetchZoneSystem(
  sport: string,
  metric: string,
): Promise<ZoneSystem | null> {
  log.debug("API", "Fetching zone system", sport, metric);
  try {
    const response = await zonesApi.getSystem(sport, metric);
    return response.data as ZoneSystem;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      log.debug("API", `No zone system for ${sport}/${metric} (not configured)`);
      return null;
    }
    log.error("API", "Failed to fetch zone system:", error);
    throw error;
  }
}

export async function bootstrapZones(): Promise<ZoneBootstrapResponse> {
  log.debug("API", "Bootstrapping zones");
  try {
    const response = await zonesApi.bootstrap();
    return response.data as ZoneBootstrapResponse;
  } catch (error) {
    log.error("API", "Failed to bootstrap zones:", error);
    throw error;
  }
}

export async function updateZoneAnchor(
  zoneSystemId: string,
  anchorValue: number,
): Promise<ZoneSystem> {
  log.debug("API", "Updating zone anchor", zoneSystemId);
  try {
    const response = await zonesApi.updateAnchor(zoneSystemId, {
      anchor_value: anchorValue,
    });
    return response.data as ZoneSystem;
  } catch (error) {
    log.error("API", "Failed to update zone anchor:", error);
    throw error;
  }
}

export interface CalibrationResult {
  status: string;
  lt2_pace_per_km: number;
  activities_analyzed: number;
}

export async function calibratePaceZones(): Promise<CalibrationResult> {
  log.debug("API", "Calibrating pace zones from history");
  try {
    const response = await zonesApi.calibrateRunPace();
    return response.data as CalibrationResult;
  } catch (error) {
    log.error("API", "Failed to calibrate pace zones:", error);
    throw error;
  }
}

// ── Daily Adaptation (Track 5) ─────────────────────────────────────────────

export interface DailyAdaptationData {
  action: string;
  confidence: number;
  original_session_type: string | null;
  modified_session_type: string | null;
  volume_multiplier: number;
  intensity_multiplier: number;
  reason_code: string;
  reason_text: string;
  explanation: string;
  can_override: boolean;
  has_changes: boolean;
  is_reduction: boolean;
}

export async function fetchTodayAdaptation(
  targetDate?: string,
): Promise<DailyAdaptationData> {
  log.debug("API", "Fetching today adaptation");
  try {
    const params: Record<string, string> = {};
    if (targetDate) params.target_date = targetDate;
    // Whitelisted path — intelligence API migration tracked separately
    const { api } = await import("./api");
    const response = await api.get("/intelligence/today/adaptation", { params });
    return response.data as DailyAdaptationData;
  } catch (error) {
    log.error("API", "Failed to fetch today adaptation:", error);
    throw error;
  }
}

// ── Session Compliance ──────────────────────────────────────────────────────

export interface SessionComplianceData {
  planned_session_id: string;
  activity_id: string | null;
  execution_status: string;
  zone_match_score: number | null;
  duration_accuracy: number | null;
  distance_accuracy: number | null;
  intensity_accuracy: number | null;
  quality_score: number;
  match_confidence: string;
  verdict: string | null;
  verdict_confidence: number | null;
  duration_delta_pct: number | null;
  distance_delta_pct: number | null;
  intensity_direction: string | null;
  flags: string[];
}

export async function fetchSessionCompliance(
  sessionId: string,
): Promise<SessionComplianceData> {
  log.debug("API", "Fetching session compliance", sessionId);
  try {
    const response = await closedLoopApi.getSessionCompliance(sessionId);
    return response.data as SessionComplianceData;
  } catch (error) {
    log.error("API", "Failed to fetch session compliance:", error);
    throw error;
  }
}

export async function createCustomZones(
  sport: string,
  metric: string,
  bands: Array<{
    zone_number: number;
    name: string;
    lower_bound: number;
    upper_bound: number;
    description?: string;
  }>,
): Promise<ZoneSystem> {
  log.debug("API", "Creating custom zones");
  try {
    const response = await zonesApi.createCustom({ sport, metric, bands });
    return response.data as ZoneSystem;
  } catch (error) {
    log.error("API", "Failed to create custom zones:", error);
    throw error;
  }
}

// ── Performance Analytics (migrated from api.ts — Track 6.3) ────────────────

export interface PaceZoneDefinition {
  zone: string;
  name: string;
  min_speed_ms: number | null;
  max_speed_ms: number | null;
  min_pace_min_per_km: number | null;
  max_pace_min_per_km: number | null;
}

export interface PerformanceAnalyticsResponse {
  power_duration_curve: {
    durations: number[];
    watts: number[];
    source_activity_ids: Array<string | null>;
    peaks: Array<{ duration_seconds: number; power: number; activity_id?: string | null }>;
  };
  zone_distribution: {
    power_zones?: Array<{ zone: string; name?: string; seconds?: number; minutes: number; percentage: number }>;
    hr_zones?: Array<{ zone: string; name?: string; seconds?: number; minutes: number; percentage: number }>;
    pace_zones?: Array<{ zone: string; name?: string; seconds?: number; minutes: number; percentage: number }>;
  };
  pace_zone_definitions?: PaceZoneDefinition[];
  threshold_pace_ms?: number | null;
  threshold_pace_source?: 'configured' | 'inferred' | null;
  peak_power_records: {
    best_5s?: unknown;
    best_1m?: unknown;
    best_5m?: unknown;
    best_20m?: unknown;
    peaks: Array<{
      duration: string;
      duration_seconds: number;
      power: number;
      date: string;
      activity_name: string;
      activity_id?: string | null;
    }>;
  };
  efficiency_metrics: {
    series: Array<{
      date: string;
      ef: number;
      aerobic_decoupling: number;
      intensity_factor?: number | null;
      variability_index?: number | null;
    }>;
    decoupling: Array<{ date: string; percent: number }>;
  };
  ingestion_audit?: {
    totals: { activities: number; with_streams: number; without_streams: number };
    missing_fields: Record<string, number>;
  };
}

export const fetchAnalyticsMetrics = async (days?: number): Promise<{
  chart: Array<{
    date: string;
    CTL: number;
    ATL: number;
    TSB: number;
    hr: number;
    dist: number;
    time: number;
  }>;
}> => {
  log.debug("API", "Fetching analytics metrics");
  try {
    const params = days ? { days } : undefined;
    const response = await closedLoopApi.getMetrics(params);
    return response as unknown as {
      chart: Array<{
        date: string;
        CTL: number;
        ATL: number;
        TSB: number;
        hr: number;
        dist: number;
        time: number;
      }>;
    };
  } catch (error) {
    log.error("API", "Failed to fetch analytics metrics:", error);
    throw error;
  }
};

export const fetchPerformanceAnalytics = async (days = 90): Promise<PerformanceAnalyticsResponse> => {
  const endDate = new Date();
  const startDate = subDays(endDate, Math.max(1, days - 1));
  const start = format(startDate, 'yyyy-MM-dd');
  const end = format(endDate, 'yyyy-MM-dd');

  const response = await closedLoopApi.getPerformance({
    start_date: start,
    end_date: end,
  });
  return response as unknown as PerformanceAnalyticsResponse;
};

export const backfillRecentActivityStreams = async (
  activityIds: string[],
): Promise<{ attempted: number; requested: number }> => {
  const ids = [...new Set(activityIds.filter((id) => typeof id === 'string' && id.length > 0))];
  let requested = 0;

  for (const id of ids) {
    try {
      requested += 1;
      await activitiesApi.fetchStreams(id);
    } catch (error) {
      log.warn("API", `Stream backfill skipped for activity ${id}:`, error);
    }
  }

  return { attempted: ids.length, requested };
};

export const fetchPowerDurationCurve = async (days = 90): Promise<{
  peaks: Array<{ duration_seconds: number; power: number }>;
}> => {
  try {
    const response = await insightsApi.getPowerCurve({ days });
    return response as unknown as { peaks: Array<{ duration_seconds: number; power: number }> };
  } catch {
    return { peaks: [] };
  }
};

export const fetchPeakPowerRecords = async (): Promise<{
  peaks: Array<{
    duration: string;
    duration_seconds: number;
    power: number;
    date: string;
    activity_name: string;
  }>;
}> => {
  try {
    const response = await insightsApi.getPeakPower();
    return response as unknown as {
      peaks: Array<{
        duration: string;
        duration_seconds: number;
        power: number;
        date: string;
        activity_name: string;
      }>;
    };
  } catch {
    return { peaks: [] };
  }
};

export const fetchEfficiencyTrend = async (weeks = 12): Promise<{
  series: Array<{
    date: string;
    ef: number;
    aerobic_decoupling: number;
    intensity_factor?: number;
    variability_index?: number;
  }>;
}> => {
  try {
    const response = await insightsApi.getEfficiency({ weeks });
    return response as unknown as {
      series: Array<{
        date: string;
        ef: number;
        aerobic_decoupling: number;
        intensity_factor?: number;
        variability_index?: number;
      }>;
    };
  } catch {
    return { series: [] };
  }
};

export const fetchZoneDistribution = async (days = 90): Promise<{
  power_zones?: Array<{ zone: string; minutes: number; percentage: number }>;
  hr_zones?: Array<{ zone: string; minutes: number; percentage: number }>;
  pace_zones?: Array<{ zone: string; minutes: number; percentage: number }>;
}> => {
  try {
    const response = await insightsApi.getZones({ days });
    return response as unknown as {
      power_zones?: Array<{ zone: string; minutes: number; percentage: number }>;
      hr_zones?: Array<{ zone: string; minutes: number; percentage: number }>;
      pace_zones?: Array<{ zone: string; minutes: number; percentage: number }>;
    };
  } catch {
    return {};
  }
};

export const fetchInsightsOverviewAdvanced = async (days = 90): Promise<{
  series: Array<{
    date: string;
    load: number;
    ctl: number;
    atl: number;
    tsb: number;
    minutes: number;
  }>;
}> => {
  try {
    const response = await insightsApi.getOverview({ days });
    return response as unknown as {
      series: Array<{
        date: string;
        load: number;
        ctl: number;
        atl: number;
        tsb: number;
        minutes: number;
      }>;
    };
  } catch {
    return { series: [] };
  }
};

export const fetchInsightsComplianceAdvanced = async (weeks = 12): Promise<{
  series: Array<{
    week_start: string;
    planned_count: number;
    completed_count: number;
    planned_minutes: number;
    completed_minutes: number;
    planned_load: number;
    completed_load: number;
  }>;
}> => {
  try {
    const response = await insightsApi.getCompliance({ weeks });
    return response as unknown as {
      series: Array<{
        week_start: string;
        planned_count: number;
        completed_count: number;
        planned_minutes: number;
        completed_minutes: number;
        planned_load: number;
        completed_load: number;
      }>;
    };
  } catch {
    return { series: [] };
  }
};

// ── Risk Assessment (migrated from api.ts — Track 6.3) ─────────────────────

export interface RiskAssessmentPayload {
  risk_band: 'normal' | 'elevated' | 'high' | 'critical';
  strain_score?: number | null;
  drivers: string[];
  recommended_action: string;
  metrics: {
    tsb: number;
    ctl: number;
    atl: number;
    ramp_rate: number;
    atl_ctl_ratio: number;
    tsb_persistence_days: number;
    ramp_persistence_checks: number;
  };
  explanation: {
    headline: string;
    summary: string;
    drivers: string[];
    next_48h: string;
    next_7d: string;
  };
}

export const fetchRiskAssessment = async (days = 90): Promise<RiskAssessmentPayload> => {
  const fallback: RiskAssessmentPayload = {
    risk_band: 'normal',
    strain_score: null,
    drivers: ['Risk engine syncing with backend data'],
    recommended_action: 'Risk guidance will refresh automatically once sync completes.',
    metrics: {
      tsb: Number.NaN,
      ctl: Number.NaN,
      atl: Number.NaN,
      ramp_rate: Number.NaN,
      atl_ctl_ratio: Number.NaN,
      tsb_persistence_days: 0,
      ramp_persistence_checks: 0,
    },
    explanation: {
      headline: 'Dialing in your risk signal.',
      summary: 'Recent load is available; risk context is catching up.',
      drivers: ['Risk signal is stabilizing from backend load history.'],
      next_48h: 'Use easy effort and prioritize recovery basics.',
      next_7d: 'Risk guidance auto-updates once backend sync is healthy.',
    },
  };
  try {
    const response = await closedLoopApi.getRiskAssessment({ days });
    return response as unknown as RiskAssessmentPayload;
  } catch (error) {
    log.warn("API", 'Failed to fetch risk assessment, using fallback', error);
    return fallback;
  }
};
